export const LEGAL_VERSION = "2.0";
export const LEGAL_UPDATED = "Version 2.0 · Post-Architectural Alignment";
export const LEGAL_ENTITY = "CARENEST, operated by Care Solutions Limited · Enugu & regional hubs, Nigeria";

export const ROLES = [
  {
    name: "Master Client",
    body:
      "Financial contributor and primary account administrator. Negotiates terms, adds Beneficiaries and Witnesses, and acts as the legal guarantor for on-site safety, property access and payment obligations. Mandatory baseline KYC required.",
  },
  {
    name: "Referral Client",
    body:
      "Paying entity restricted strictly to funding. Does not actively supervise sessions on-site. Upgrades automatically to Master Client status if actively supervising or adding witnesses.",
  },
  {
    name: "Referrer",
    body:
      "Non-paying entity who invites prospective clients. Earns flat referral bonuses (₦2,000 for Individual/Group; ₦5,000 for Business/Hospital/NGO) funded directly out of CARENEST's facilitation fee pool upon completion of the client's first session, after the 24-hour dispute window. Eligible to serve as a Witness.",
  },
  {
    name: "Witness",
    body:
      "Non-paying on-site supervisor (family member, neighbour, referrer or local caregiver). Receives the visiting practitioner on-site, visually inspects profile details, receives the 4-digit Termii SMS PIN and reads it to the provider to confirm physical arrival.",
  },
  {
    name: "Service Provider",
    body:
      "Independent, MRTB- or NMCN-regulated health professional (Physical Therapist or Registered Nurse). Non-paying entity required to complete rigorous KYC — licence photo, serial number, biometrics and government ID. Responsible for visual verification upon arrival and triggering session state logs.",
  },
];

export const CANCELLATION_MATRIX = {
  head: ["Cancellation scenario", "Provider payout status", "Client refund allocation", "CARENEST fee (3%)"],
  rows: [
    ["Client timely cancellation (more than 2 hours before the session)", "No payout", "100% pre-authorisation hold release (0% deduction)", "0% — fee waived"],
    ["Client late cancellation (less than 2 hours before the session)", "97% disbursement (travel / inconvenience fee)", "0% refund — card pre-authorisation captured in full", "3% retained by the platform"],
    ["Provider cancels at the doorstep (safety / misconduct)", "No payout", "100% automated pre-authorisation hold drop / refund", "0% retained"],
    ["Successful session arrival (Witness PIN confirmed)", "97% full session payout", "Balance minus bank charges", "3% full commission retained"],
    ["Mid-contract cancellation (with valid audit proof)", "97% on rendered sessions", "Unrendered sessions balance minus bank fee", "3% on rendered sessions"],
  ],
};

export const FEE_SECTIONS = [
  {
    title: "97 / 3 settlement split",
    paras: [
      "CARENEST implements a strict 97/3 revenue disbursement model upon successful verification of session execution, or upon an applicable late cancellation trigger.",
      "Provider disbursement share — 97% of the session value is disbursed directly to the verified Service Provider's bank account. This acts as their professional service fee and inconvenience / travel compensation.",
      "CARENEST platform facilitation fee — 3% of the session value is retained in CARENEST's corporate account to cover technology maintenance, Termii routing costs, customer support and platform facilitation resources.",
    ],
  },
  {
    title: "For Service Providers — third-party processing & settlement charges",
    paras: [
      "CARENEST charges a flat 3% platform facilitation fee on all rendered session base rates. Standard payment gateway processing fees (Paystack) and interbank transfer charges (NIBSS / bank fees) are applied directly by Paystack and the commercial banking system upon payout disbursement.",
      "CARENEST does not control or retain these statutory third-party banking charges.",
    ],
  },
  {
    title: "For paying Clients — fee breakdown transparency",
    paras: [
      "All bookings reflect the practitioner's net session rate plus CARENEST's 3% platform fee. Payment gateway processing charges (Paystack card or bank transfer fees) and applicable statutory banking duties are added dynamically at checkout at the rates determined by Paystack and commercial banking institutions.",
      "Bank and escrow processing charges are split evenly (50/50) between the paying client and CARENEST as part of the checkout fee breakdown.",
    ],
  },
  {
    title: "Zero cash custody",
    paras: [
      "CARENEST operates strictly as a digital referee, orchestrator and report provider. Under Nigerian banking regulations the platform avoids liability as a financial custodian by maintaining a Zero Cash Custody architecture.",
      "CARENEST does not take direct custody of public deposits, run closed-loop consumer wallets, or execute arbitrary ledger clearing on proprietary servers. All payment flows are routed through Tier-1 Deposit Money Banks and CBN-licensed payment engine gateways (Paystack Engine) using card pre-authorisations (holds) and programmatic manual capture triggers.",
    ],
  },
];

export const PRIVACY_SECTIONS = [
  {
    title: "1. Statutory compliance framework",
    paras: [
      "CARENEST processes personal, biometric, geolocation and health data in strict compliance with the Nigeria Data Protection Act (NDPA), the Nigeria Data Protection Regulation (NDPR) and the National Health Act.",
      "Processing is conducted under the lawful bases of contractual necessity, legal obligation and explicit data subject consent.",
    ],
  },
  {
    title: "2. Dynamic profile directory & temporal data isolation",
    paras: [
      "To eliminate unnecessary privacy exposure and mitigate security risks for patients, witnesses and clinicians, CARENEST enforces a dynamic temporal data access scope.",
      "Directory generation window — the Profile Directory (containing patient medical conditions, exact delivery address, geolocation and Witness details) is dynamically generated and compiled for the Service Provider's application interface exactly 2 hours before the scheduled session start time.",
      "Offline local caching — the provider application pre-caches and downloads Witness photos locally 1 hour before the session start time, so that visual identity checks can still occur at the doorstep if mobile network connectivity drops.",
      "Automatic access revocation and purge — once the appointment moves to VERIFIED_ARRIVAL_PAYOUT_SECURED, COMPLETED or CANCELLED, access to the dynamic directory is immediately revoked and local caches on the provider's device are automatically purged.",
    ],
  },
  {
    title: "3. Anti-fraud data sanitisation & witness farming controls",
    paras: [
      "Unique phone number enforcement — every Witness added to a Master Client's matrix must have a unique, verified phone number. The database schema restricts duplicate phone number entries across conflicting client and provider accounts.",
      "Cryptographic audit trails — every Termii SMS token request, PIN entry, timestamp and visual check event writes a cryptographic record containing device IDs, IP addresses and exact geolocations. This audit log serves as definitive evidence in law enforcement, insurance or dispute scenarios.",
    ],
  },
  {
    title: "4. Multi-channel communications & quiet hours",
    paras: [
      "Trigger events — booking created, pre-authorisation held, 2-hour directory live reminder, session complete, 24-hour dispute warning, dispute filed, payout disbursed and referral bonus earned.",
      "DND channel routing — verification SMS tokens are routed via Termii's DND-whitelisted channel to guarantee immediate delivery across all Nigerian networks regardless of a user's Do-Not-Disturb settings.",
      "Enforced quiet hours — automated marketing, non-urgent transactional notifications and system alerts are strictly suppressed between 10:00 PM and 7:00 AM local time, except for active, real-time OTP verification requests triggered during a live late-night or early-morning session window.",
    ],
  },
];

export const LEGAL_CONTACTS = {
  privacy: "privacy@carenest.com.ng",
  legal: "legal@carenest.com.ng",
};
